<template>
  <!-- 챗봇 토글 버튼 -->
  <div
    v-if="!isOpen"
    class="chatbot-toggle"
    @click="openChat"
    title="도움이 필요하신가요?"
  >
    <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
      <circle cx="8" cy="10" r="1.2"/>
      <circle cx="12" cy="10" r="1.2"/>
      <circle cx="16" cy="10" r="1.2"/>
    </svg>
    <span v-if="!hasInteracted" class="toggle-badge">?</span>
  </div>

  <!-- 챗봇 창 -->
  <transition name="chatbot-slide">
    <div v-if="isOpen" class="chatbot-window">
      <!-- 헤더 -->
      <div class="chatbot-header">
        <div class="chatbot-header-info">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="white">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <span>Smartwork 도우미</span>
        </div>
        <button class="chatbot-close" @click="closeChat" title="닫기">&times;</button>
      </div>

      <!-- 메시지 영역 -->
      <div ref="messagesContainer" class="chatbot-messages">
        <div
          v-for="(msg, idx) in messages"
          :key="idx"
          :class="['chatbot-msg', msg.type]"
        >
          <!-- 봇 아바타 -->
          <div v-if="msg.type === 'bot'" class="msg-avatar">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="#409eff">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div class="msg-content">
            <!-- eslint-disable-next-line vue/no-v-html -- DOMPurify로 새니타이징된 값만 바인딩됨 (utils/sanitizeHtml.js) -->
            <div v-if="msg.html" v-html="sanitizeHtml(msg.html)"></div>
            <div v-else>{{ msg.text }}</div>

            <!-- 관련 질문 -->
            <div v-if="msg.relatedItems && msg.relatedItems.length > 0" class="related-items">
              <p class="related-title">관련 도움말:</p>
              <button
                v-for="item in msg.relatedItems"
                :key="item.id"
                class="related-btn"
                @click="handleRelatedClick(item)"
              >
                {{ item.question }}
              </button>
            </div>

            <!-- 빠른 이동 메뉴 -->
            <div v-if="msg.menus && msg.menus.length > 0" class="quick-menus">
              <button
                v-for="menu in msg.menus"
                :key="menu.path"
                class="menu-btn"
                @click="handleMenuClick(menu)"
              >
                {{ menu.label }}
              </button>
            </div>

            <!-- 카테고리 버튼 -->
            <div v-if="msg.categories" class="category-btns">
              <button
                v-for="cat in msg.categories"
                :key="cat"
                class="category-btn"
                @click="handleCategoryClick(cat)"
              >
                {{ cat }}
              </button>
            </div>
          </div>
        </div>

        <!-- 타이핑 표시 -->
        <div v-if="isTyping" class="chatbot-msg bot">
          <div class="msg-avatar">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="#409eff">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div class="msg-content typing">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>
      </div>

      <!-- 빠른 질문 제안 (초기) -->
      <div v-if="messages.length <= 1" class="quick-suggestions">
        <button
          v-for="q in quickQuestions"
          :key="q"
          class="suggestion-btn"
          @click="sendMessage(q)"
        >
          {{ q }}
        </button>
      </div>

      <!-- 입력 영역 -->
      <div class="chatbot-input">
        <input
          ref="inputRef"
          v-model="userInput"
          type="text"
          placeholder="궁금한 것을 입력하세요..."
          @keyup.enter="handleSend"
          :disabled="isTyping"
        />
        <button
          class="send-btn"
          @click="handleSend"
          :disabled="!userInput.trim() || isTyping"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { sanitizeHtml } from '@/utils/sanitizeHtml'
import {
  searchKnowledge,
  searchMenus,
  getKnowledgeById,
  getByCategory,
  getCategories,
  greetings,
  fallbackResponse
} from './chatbotKnowledge'

const router = useRouter()

const isOpen = ref(false)
const hasInteracted = ref(false)
const userInput = ref('')
const messages = ref([])
const isTyping = ref(false)
const messagesContainer = ref(null)
const inputRef = ref(null)

const quickQuestions = [
  '전체 업무 흐름이 뭐예요?',
  '메인오더 어떻게 만드나요?',
  '권한이 없다고 나와요',
  '화면이 안 나와요',
  '도움말 보는 방법'
]

// 챗봇 열기
const openChat = () => {
  isOpen.value = true
  hasInteracted.value = true
  if (messages.value.length === 0) {
    addBotMessage({
      html: `<p><b>${greetings[Math.floor(Math.random() * greetings.length)]}</b></p>
<p>아래 자주 묻는 질문을 선택하거나, 직접 질문을 입력해주세요.</p>`,
      categories: getCategories()
    })
  }
  nextTick(() => {
    inputRef.value?.focus()
  })
}

// 챗봇 닫기
const closeChat = () => {
  isOpen.value = false
}

// 메시지 추가
const addUserMessage = (text) => {
  messages.value.push({ type: 'user', text })
  scrollToBottom()
}

const addBotMessage = (opts) => {
  messages.value.push({
    type: 'bot',
    html: opts.html || null,
    text: opts.text || null,
    relatedItems: opts.relatedItems || [],
    menus: opts.menus || [],
    categories: opts.categories || null
  })
  scrollToBottom()
}

// 스크롤
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

// 사용자 입력 처리
const handleSend = () => {
  const text = userInput.value.trim()
  if (!text) return
  sendMessage(text)
  userInput.value = ''
}

// 메시지 발송 및 답변 생성
const sendMessage = async (text) => {
  addUserMessage(text)
  isTyping.value = true
  scrollToBottom()

  // 타이핑 효과를 위한 딜레이
  await new Promise(resolve => setTimeout(resolve, 400 + Math.random() * 400))

  isTyping.value = false

  // 인사말 체크
  const greetWords = ['안녕', '하이', '헬로', 'hi', 'hello', '반갑']
  if (greetWords.some(w => text.toLowerCase().includes(w)) && text.length < 10) {
    addBotMessage({
      html: `<p>안녕하세요! Smartwork 도우미입니다. 무엇을 도와드릴까요?</p>`,
      categories: getCategories()
    })
    return
  }

  // "메뉴", "페이지", "이동", "가는법" 등 네비게이션 질문
  const navWords = ['메뉴', '이동', '가는법', '어디', '위치', '화면', '페이지로']
  const isNavQuestion = navWords.some(w => text.includes(w))
  const menuResults = searchMenus(text)

  // 지식베이스 검색
  const results = searchKnowledge(text)

  if (results.length > 0) {
    const best = results[0]

    // 관련 항목 가져오기
    const relatedItems = (best.relatedIds || [])
      .map(id => getKnowledgeById(id))
      .filter(Boolean)
      .slice(0, 3)

    addBotMessage({
      html: best.answer,
      relatedItems,
      menus: isNavQuestion ? menuResults.slice(0, 4) : []
    })

    // 추가 결과가 있으면 제안
    if (results.length > 1) {
      const otherResults = results.slice(1, 4)
      addBotMessage({
        html: `<p style="color: #909399; font-size: 12px;">다른 관련 도움말도 있어요:</p>`,
        relatedItems: otherResults
      })
    }
  } else if (menuResults.length > 0) {
    addBotMessage({
      html: `<p>관련 메뉴를 찾았습니다. 아래 버튼을 클릭하면 해당 화면으로 이동합니다:</p>`,
      menus: menuResults.slice(0, 6)
    })
  } else {
    // 답변 못 찾음
    addBotMessage({
      html: fallbackResponse,
      categories: getCategories()
    })
  }
}

// 관련 질문 클릭
const handleRelatedClick = (item) => {
  const relatedItems = (item.relatedIds || [])
    .map(id => getKnowledgeById(id))
    .filter(Boolean)
    .slice(0, 3)

  addUserMessage(item.question)
  addBotMessage({
    html: item.answer,
    relatedItems
  })
}

// 메뉴 클릭 → 페이지 이동
const handleMenuClick = (menu) => {
  closeChat()
  router.push(menu.path)
}

// 카테고리 클릭
const handleCategoryClick = (category) => {
  const items = getByCategory(category)
  if (items.length > 0) {
    addUserMessage(`${category} 관련 도움말`)
    addBotMessage({
      html: `<p><b>"${category}"</b> 관련 도움말 목록입니다:</p>`,
      relatedItems: items.slice(0, 6)
    })
  }
}

onMounted(() => {
  // 5초 후에 뱃지 표시를 위한 플래그 초기화 (선택적)
})
</script>

<style scoped>
/* ===== 토글 버튼 ===== */
.chatbot-toggle {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #409eff, #337ecc);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.4);
  z-index: 9998;
  transition: all 0.3s ease;
}

.chatbot-toggle:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(64, 158, 255, 0.5);
}

.toggle-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 20px;
  height: 20px;
  background: #f56c6c;
  color: white;
  border-radius: 50%;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

/* ===== 챗봇 창 ===== */
.chatbot-window {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 380px;
  height: 560px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ===== 헤더 ===== */
.chatbot-header {
  background: linear-gradient(135deg, #409eff, #337ecc);
  color: white;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.chatbot-header-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 15px;
}

.chatbot-close {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.chatbot-close:hover {
  opacity: 1;
}

/* ===== 메시지 영역 ===== */
.chatbot-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chatbot-msg {
  display: flex;
  gap: 8px;
  max-width: 92%;
}

.chatbot-msg.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.chatbot-msg.bot {
  align-self: flex-start;
}

.msg-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #ecf5ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.msg-content {
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.6;
  word-break: break-word;
}

.chatbot-msg.bot .msg-content {
  background: #f5f7fa;
  color: #303133;
  border-top-left-radius: 4px;
}

.chatbot-msg.user .msg-content {
  background: #409eff;
  color: white;
  border-top-right-radius: 4px;
}

/* HTML 내 스타일 */
.msg-content :deep(b) {
  color: #303133;
}

.msg-content :deep(ol),
.msg-content :deep(ul) {
  padding-left: 18px;
  margin: 6px 0;
}

.msg-content :deep(li) {
  margin-bottom: 4px;
}

.msg-content :deep(p) {
  margin: 4px 0;
}

/* ===== 타이핑 애니메이션 ===== */
.msg-content.typing {
  display: flex;
  gap: 4px;
  padding: 12px 18px;
}

.dot {
  width: 8px;
  height: 8px;
  background: #c0c4cc;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out;
}

.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

/* ===== 관련 항목 ===== */
.related-items {
  margin-top: 10px;
  border-top: 1px solid #e4e7ed;
  padding-top: 8px;
}

.related-title {
  font-size: 11px;
  color: #909399;
  margin-bottom: 6px;
}

.related-btn {
  display: block;
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  margin-bottom: 4px;
  background: #ecf5ff;
  color: #409eff;
  border: 1px solid #d9ecff;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.related-btn:hover {
  background: #409eff;
  color: white;
}

/* ===== 빠른 메뉴 ===== */
.quick-menus {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.menu-btn {
  padding: 5px 12px;
  background: #67c23a;
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.menu-btn:hover {
  background: #529b2e;
}

/* ===== 카테고리 버튼 ===== */
.category-btns {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.category-btn {
  padding: 5px 12px;
  background: white;
  color: #409eff;
  border: 1px solid #409eff;
  border-radius: 14px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.category-btn:hover {
  background: #409eff;
  color: white;
}

/* ===== 빠른 제안 ===== */
.quick-suggestions {
  padding: 8px 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border-top: 1px solid #ebeef5;
  flex-shrink: 0;
}

.suggestion-btn {
  padding: 5px 10px;
  background: #fdf6ec;
  color: #e6a23c;
  border: 1px solid #faecd8;
  border-radius: 14px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.suggestion-btn:hover {
  background: #e6a23c;
  color: white;
}

/* ===== 입력 영역 ===== */
.chatbot-input {
  display: flex;
  padding: 12px;
  border-top: 1px solid #ebeef5;
  gap: 8px;
  flex-shrink: 0;
}

.chatbot-input input {
  flex: 1;
  padding: 8px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 20px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;
}

.chatbot-input input:focus {
  border-color: #409eff;
}

.send-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #409eff;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.send-btn:hover:not(:disabled) {
  background: #337ecc;
}

.send-btn:disabled {
  background: #c0c4cc;
  cursor: not-allowed;
}

/* ===== 트랜지션 ===== */
.chatbot-slide-enter-active {
  animation: chatbot-in 0.3s ease;
}

.chatbot-slide-leave-active {
  animation: chatbot-out 0.2s ease;
}

@keyframes chatbot-in {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes chatbot-out {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
}

/* ===== 반응형 ===== */
@media (max-width: 768px) {
  .chatbot-window {
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .chatbot-toggle {
    bottom: 16px;
    right: 16px;
    width: 50px;
    height: 50px;
  }
}

/* ===== 스크롤바 ===== */
.chatbot-messages::-webkit-scrollbar {
  width: 4px;
}

.chatbot-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chatbot-messages::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 2px;
}
</style>
